"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { BasaltMark } from "./basalt-mark";
import { CartMenu } from "./cart/cart-menu";
import { AuthMenu } from "./auth/auth-menu";
import { CurrencySelect } from "./currency/currency-select";
import { formatMoney } from "@/lib/currency";
import type { FromPrice } from "@/lib/products";

const SECTIONS = ["top", "story", "design", "spec", "compare", "reviews", "support"];

// Last-resort only -- shown if the catalog fetch in page.tsx's getFromPrice() fails or the catalog
// is empty. Not the normal path; see StickyBuyBar's matching constant.
const FALLBACK_FROM_PRICE: FromPrice = { amount: 49, currency: "AUD" };

// Same-page anchors -- scroll-spy applies to these only. "Buy" is deliberately
// separate: it's a real route (/buy), not a section of this page, since the
// story page carries no purchase/config UI of its own -- see
// design/site-map-and-navigation.md and design/product-page-structure.md.
const NAV_LINKS = [
  { href: "#story", label: "Story" },
  { href: "#design", label: "Design" },
  { href: "#spec", label: "Spec" },
  { href: "#reviews", label: "Reviews" },
  { href: "#support", label: "Support" },
];

export function SiteNav({ fromPrice }: { fromPrice: FromPrice | null }) {
  const price = fromPrice ?? FALLBACK_FROM_PRICE;
  const [activeId, setActiveId] = useState<string | null>(null);
  const [showBuy, setShowBuy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const sectionEls = SECTIONS.map((id) => document.getElementById(id)).filter(
      (el): el is HTMLElement => el !== null
    );

    const sectionIO = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px" }
    );
    sectionEls.forEach((el) => sectionIO.observe(el));

    // Sticky buy button: plain scroll-position check rather than an
    // IntersectionObserver on the hero -- simpler to reason about, and
    // avoids IO's edge cases around hydration timing/rootMargin on a
    // full-bleed hero. Shows once scrolled past the hero's own height.
    const hero = document.getElementById("top");
    const updateShowBuy = () => {
      if (!hero) return;
      setShowBuy(window.scrollY > hero.offsetHeight - 64);
    };
    updateShowBuy();
    window.addEventListener("scroll", updateShowBuy, { passive: true });
    window.addEventListener("resize", updateShowBuy);

    return () => {
      sectionIO.disconnect();
      window.removeEventListener("scroll", updateShowBuy);
      window.removeEventListener("resize", updateShowBuy);
    };
  }, []);

  const menuRef = useRef<HTMLDivElement>(null);

  return (
    <nav className="site-nav">
      <div className="wrap">
        <span className="brand-lockup">
          <BasaltMark size={24} />
          <span className="word">BASALT</span>
        </span>
        <ul className="nav-links">
          {NAV_LINKS.map((link) => (
            <li key={link.href}>
              <a
                href={link.href}
                className={activeId === link.href.slice(1) ? "active" : undefined}
              >
                {link.label}
              </a>
            </li>
          ))}
        </ul>
        <Link href="/buy" className={`nav-buy${showBuy ? " show" : ""}`}>
          {/* Floored, not rounded -- "from $X" should never overstate the real cheapest price. */}
          Buy — from {formatMoney(Math.floor(price.amount), price.currency).replace(/\.00$/, "")}
        </Link>
        <div className="nav-icons">
          <AuthMenu />
          <CartMenu />
        </div>
        <button
          className="hamburger"
          aria-label="Menu"
          aria-expanded={menuOpen}
          aria-controls="mobileMenu"
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span />
          <span />
          <span />
        </button>
      </div>
      <div
        id="mobileMenu"
        ref={menuRef}
        className={`mobile-menu${menuOpen ? " open" : ""}`}
      >
        {NAV_LINKS.map((link) => (
          <a key={link.href} href={link.href} onClick={() => setMenuOpen(false)}>
            {link.label}
          </a>
        ))}
        <Link href="/buy" onClick={() => setMenuOpen(false)}>
          Buy
        </Link>
      </div>
    </nav>
  );
}
